import { useUser } from '@r/api/auth.tsx';
import { type LinkProps, useLocation } from '@tanstack/react-router';
import type { UserWorkspaceMeReadModel } from '@r/api/generated/client.schemas.ts';

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarRail
} from '@ui/sidebar.tsx';
import { WorkspaceSwitcher } from '@admin/components/workspace/workspace-switcher.tsx';
import { NavUser } from '@r/components/nav-user.tsx';
import { NavMain } from '@admin/components/navigation/nav-main.tsx';

import {
  ArchiveIcon,
  ShieldUser,
  GalleryVerticalEnd,
  type LucideIcon,
  LayoutDashboardIcon
} from 'lucide-react';

import { useRoutes } from '@r/hooks/use-routes.ts';
import { linksAvailable } from '@r/utils/utils.ts';
import { getActiveWorkspaceUniversal } from '@r/helpers/api.ts';
import { useTranslation } from 'react-i18next';
interface MenuItemBase {
  title: string;
  icon?: LucideIcon;
  isActive?: boolean;
  isVisible?: boolean;
}

interface LeafMenuItem extends MenuItemBase {
  url: LinkProps['to'];
  items?: never;
}

interface ParentMenuItem extends MenuItemBase {
  url: LinkProps['to'] | '#';
  items: LeafMenuItem[];
}

type MenuItem = ParentMenuItem | LeafMenuItem;
type UserWorkspace = UserWorkspaceMeReadModel;

function isCurrentRoute(pathname: string, route: string | string[]) {
  const paths = Array.isArray(route) ? route : [route];

  return paths.some((path) => {
    if (path.endsWith('/*') || path.endsWith('/:id') || path.endsWith('/$id')) {
      const base = path.replace(/\/(\*|:id)$/, '');

      if (path.endsWith('/*')) {
        return pathname === base || pathname.startsWith(`${base}/`);
      }
      return new RegExp(`^${base}/\\d+$`).test(pathname);
    }
    return pathname === path;
  });
}

function useFiddyAdminData(): { navMain: MenuItem[] } {
  const allRoutes = useRoutes();
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const isVisibleRoutes = (routes: string[]) => {
    const links = linksAvailable(allRoutes.data, routes);
    return links.length > 0;
  };

  return {
    navMain: [
      {
        title: t('fiddy.admin.sidebar.title'),
        url: '/fiddy-admin',
        icon: LayoutDashboardIcon,
        isActive: isCurrentRoute(pathname, '/fiddy-admin'),
        isVisible: isVisibleRoutes(['/']),
        items: []
      },
      {
        title: t('fiddy.admin.sidebar.translations.languages.title'),
        url: '#',
        icon: ShieldUser,
        isActive: isCurrentRoute(pathname, '/fiddy-admin/translations/*'),
        isVisible: isVisibleRoutes(['/fiddy-admin', '/fiddy-admin/translations']),
        items: [
          {
            title: t('fiddy.admin.sidebar.translations.title'),
            url: '/fiddy-admin/translations',
            isActive: isCurrentRoute(pathname, '/fiddy-admin/translations')
          }
        ]
      },
      {
        title: t('fiddy.admin.sidebar.resources'),
        url: '#',
        icon: ArchiveIcon,
        isActive: isCurrentRoute(pathname, '/fiddy-admin/ressources/*'),
        isVisible: isVisibleRoutes([
          '/fiddy-admin/ressources/changelogs',
          '/fiddy-admin/ressources/video-instructions'
        ]),
        items: [
          {
            title: t('fiddy.admin.changelog.menu.title'),
            url: '/fiddy-admin/ressources/changelogs',
            isActive: isCurrentRoute(pathname, '/fiddy-admin/ressources/changelogs')
          },
          {
            title: t('fiddy.admin.video.notices.menu.title'),
            url: '/fiddy-admin/ressources/video-instructions',
            isActive: isCurrentRoute(pathname, '/fiddy-admin/ressources/video-instructions')
          }
        ]
      }
    ]
  };
}

function getWorkspaceSelectOptions(userWorkspaces: UserWorkspace[]) {
  return userWorkspaces.map((workspace) => ({
    active: workspace.active,
    name: workspace.workspace.name,
    logo: GalleryVerticalEnd,
    plan: 'Workspace',
    value: workspace.workspace.id,
    roles: workspace.roles.filter((role): role is string => role != null)
  }));
}

export function FiddyAdminSidebar() {
  const user = useUser();
  const { navMain } = useFiddyAdminData();
  const userWorkspaces: UserWorkspace[] = user.data?.visibleWorkspaces ?? [];
  const activeWorkspace = getActiveWorkspaceUniversal(userWorkspaces);

  function getUserFullName() {
    return `${user.data?.firstname ?? ''} ${user.data?.lastname ?? ''}`.trim();
  }

  return (
    <Sidebar collapsible="icon" className="bg-sidebar">
      {activeWorkspace && (
        <SidebarHeader>
          <WorkspaceSwitcher workspaces={getWorkspaceSelectOptions(userWorkspaces)} />
        </SidebarHeader>
      )}

      <SidebarContent>
        <NavMain items={navMain} />
      </SidebarContent>

      <SidebarFooter>
        <NavUser
          user={{
            name: getUserFullName(),
            email: user.data?.email ?? '',
            avatar: user.data?.avatar ?? null
          }}
        />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
